import React, { useState, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { itemService } from '../services/item';
import { kitchenService } from '../services/kitchen';
import { itemCategoryService } from '../services/itemCategory';
import { VegType, ItemCategory, ItemVariant, ModifierGroup } from '../types';
import { getActiveRestaurantId } from '../utils/roleAccess';
import { UtensilsCrossed, Search, Plus, Edit, Trash2, X, Settings, Check, ImagePlus } from 'lucide-react';
import { clsx } from 'clsx';
import { ConfirmationModal } from '../components/ConfirmationModal';
import { useForm, Controller } from 'react-hook-form';
import { UiSelect } from '../components/UiSelect';
import toast from 'react-hot-toast';
import { PageSkeleton, EmptyState, ButtonLoader } from '../components/LoadingStates';
import VegIcon from '../assets/Veg.png';
import NonVegIcon from '../assets/Non_veg.png';

interface CreateItemForm {
  name: string;
  price: number;
  kitchenId: string;
  categoryId: string;
  vegType: VegType;
  available: boolean;
  gstPercent: number;
  hsnCode?: string;
}

export const Items: React.FC = () => {
  const { user } = useAuth();
  const { currentTheme } = useTheme();
  const queryClient = useQueryClient();
  const activeRestaurantId = getActiveRestaurantId() || undefined;
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedKitchen, setSelectedKitchen] = useState('');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<any>(null);
  const [confirmationModal, setConfirmationModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
    type: 'warning' | 'danger' | 'info';
    confirmText: string;
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
    type: 'warning',
    confirmText: 'Confirm'
  });
  const [selectedVegType, setSelectedVegType] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<ItemCategory | null>(null);
  const [categoryName, setCategoryName] = useState('');
  const [uploadingImageItemId, setUploadingImageItemId] = useState<string | null>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const [variants, setVariants] = useState<ItemVariant[]>([]);
  const [modifierGroups, setModifierGroups] = useState<ModifierGroup[]>([]);

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors }
  } = useForm<CreateItemForm>();

  // Clear the variant/modifier editors whenever the item modal closes
  useEffect(() => {
    if (!isCreateModalOpen) { setVariants([]); setModifierGroups([]); }
  }, [isCreateModalOpen]);

  // Clean editor state into a payload (drop blank rows/options)
  const buildOptions = () => {
    const cleanVariants = variants
      .map(v => ({ name: v.name.trim(), price: Number(v.price) || 0 }))
      .filter(v => v.name);
    const cleanGroups = modifierGroups
      .map(g => ({
        name: g.name.trim(),
        required: !!g.required,
        minSelect: g.minSelect != null ? Number(g.minSelect) : undefined,
        maxSelect: g.maxSelect != null ? Number(g.maxSelect) : undefined,
        options: (g.options || [])
          .map(o => ({ name: o.name.trim(), price: Number(o.price) || 0 }))
          .filter(o => o.name),
      }))
      .filter(g => g.name && g.options.length > 0);
    return { variants: cleanVariants, modifierGroups: cleanGroups };
  };

  // Fetch items
  const { data: items, isLoading } = useQuery(
    ['items', user?.organizationCode, activeRestaurantId, selectedKitchen, selectedVegType, selectedCategory],
    () => itemService.getItems({
      org: user?.organizationCode!,
      ...(activeRestaurantId && { restaurantId: activeRestaurantId }),
      ...(selectedKitchen && { kitchenId: selectedKitchen }),
      ...(selectedVegType && { vegType: selectedVegType as any }),
      ...(selectedCategory && { categoryId: selectedCategory }),
    }),
    { enabled: !!user?.organizationCode }
  );

  // Fetch kitchens for filter
  const { data: kitchens } = useQuery(
    ['kitchens', user?.organizationCode, activeRestaurantId],
    () => kitchenService.getKitchens(user?.organizationCode!, activeRestaurantId),
    { enabled: !!user?.organizationCode }
  );

  // Fetch categories
  const { data: categories } = useQuery(
    ['itemCategories', user?.organizationCode, activeRestaurantId],
    () => itemCategoryService.getCategories(user?.organizationCode!, activeRestaurantId),
    { enabled: !!user?.organizationCode }
  );

  // Create category mutation
  const createCategoryMutation = useMutation(
    (data: { name: string; organizationCode: string }) => itemCategoryService.createCategory(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['itemCategories']);
        toast.success('Category created successfully');
        setCategoryName('');
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.message || 'Failed to create category');
      }
    }
  );

  // Update category mutation
  const updateCategoryMutation = useMutation(
    ({ id, data }: { id: string; data: { name: string } }) => itemCategoryService.updateCategory(id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['itemCategories']);
        toast.success('Category updated successfully');
        setEditingCategory(null);
        setCategoryName('');
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.message || 'Failed to update category');
      }
    }
  );

  // Toggle category status mutation
  const toggleCategoryMutation = useMutation(
    (id: string) => itemCategoryService.toggleCategoryStatus(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['itemCategories']);
        toast.success('Category status updated');
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.message || 'Failed to update category status');
      }
    }
  );

  // Delete category mutation
  const deleteCategoryMutation = useMutation(
    (id: string) => itemCategoryService.hardDeleteCategory(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['itemCategories']);
        toast.success('Category deleted successfully');
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.message || 'Failed to delete category');
      }
    }
  );

  // Create item mutation
  const createMutation = useMutation(itemService.createItem, {
    onSuccess: () => {
      queryClient.invalidateQueries(['items']);
      setIsCreateModalOpen(false);
      reset();
      toast.success('Item created successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to create item');
    }
  });

  // Update item mutation
  const updateMutation = useMutation(
    ({ id, data }: { id: string; data: any }) => itemService.updateItem(id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['items']);
        setIsCreateModalOpen(false);
        setEditingItem(null);
        reset();
        toast.success('Item updated successfully');
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.message || 'Failed to update item');
      }
    }
  );

  // Delete item mutation
  const deleteMutation = useMutation(itemService.deleteItem, {
    onSuccess: () => {
      queryClient.invalidateQueries(['items']);
      toast.success('Item deleted successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to delete item');
    }
  });

  // Toggle availability mutation
  const toggleAvailabilityMutation = useMutation(
    ({ id, available }: { id: string; available: boolean }) =>
      itemService.updateItemAvailability(id, available),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['items']);
        toast.success('Item availability updated');
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.message || 'Failed to update availability');
      }
    }
  );

  // Upload image mutation
  const uploadImageMutation = useMutation(
    ({ id, file }: { id: string; file: File }) => itemService.uploadImage(id, file),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['items']);
        toast.success('Image uploaded');
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.message || 'Failed to upload image');
      },
      onSettled: () => setUploadingImageItemId(null)
    }
  );

  // Delete image mutation
  const deleteImageMutation = useMutation(
    ({ id, url }: { id: string; url: string }) => itemService.deleteImage(id, url),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['items']);
        toast.success('Image removed');
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.message || 'Failed to remove image');
      }
    }
  );

  const handleImageUpload = (itemId: string, file: File) => {
    const allowed = ['image/jpeg', 'image/jpg', 'image/png'];
    if (!allowed.includes(file.type.toLowerCase())) {
      toast.error('Only JPG and PNG images are allowed');
      return;
    }
    setUploadingImageItemId(itemId);
    uploadImageMutation.mutate({ id: itemId, file });
  };

  const filteredItems = items?.filter(item =>
    item.name.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  const vegTypes = ['VEG', 'NON_VEG', 'VEGAN'];
  
  const onSubmit = (data: CreateItemForm) => {
    if (!activeRestaurantId) {
      toast.error('Please select a restaurant before adding or editing items.');
      return;
    }
    const { variants: cleanVariants, modifierGroups: cleanGroups } = buildOptions();
    if (editingItem) {
      // Update existing item
      updateMutation.mutate({
        id: editingItem.id,
        data: {
          name: data.name,
          kitchenId: data.kitchenId,
          categoryId: data.categoryId,
          vegType: data.vegType as VegType,
          price: data.price,
          available: data.available,
          gstPercent: data.gstPercent,
          hsnCode: data.hsnCode,
          organizationCode: user?.organizationCode!,
          variants: cleanVariants,
          modifierGroups: cleanGroups,
        }
      });
    } else {
      // Create new item
      createMutation.mutate({
        name: data.name,
        kitchenId: data.kitchenId,
        categoryId: data.categoryId,
        vegType: data.vegType as VegType,
        price: data.price,
        gstPercent: data.gstPercent,
        hsnCode: data.hsnCode,
        organizationCode: user?.organizationCode!,
        ...(activeRestaurantId && { restaurantId: activeRestaurantId }),
        variants: cleanVariants,
        modifierGroups: cleanGroups,
      });
    }
  };

  const handleToggleAvailability = (item: any) => {
    const action = item.available ? 'make unavailable' : 'make available';
    setConfirmationModal({
      isOpen: true,
      title: `${item.available ? 'Make Unavailable' : 'Make Available'}`,
      message: `Are you sure you want to ${action} "${item.name}"?`,
      onConfirm: () => {
        toggleAvailabilityMutation.mutate({
          id: item.id,
          available: !item.available
        });
        setConfirmationModal(prev => ({ ...prev, isOpen: false }));
      },
      type: item.available ? 'warning' : 'info',
      confirmText: item.available ? 'Make Unavailable' : 'Make Available'
    });
  };

  const handleEdit = (item: any) => {
    setEditingItem(item);
    reset({
      name: item.name,
      price: item.price,
      kitchenId: item.kitchenId,
      categoryId: item.categoryId || '',
      vegType: item.vegType,
      available: item.available,
      gstPercent: item.gstPercent,
      hsnCode: item.hsnCode
    });
    setVariants(Array.isArray(item.variants) ? item.variants.map((v: ItemVariant) => ({ ...v })) : []);
    setModifierGroups(Array.isArray(item.modifierGroups)
      ? item.modifierGroups.map((g: ModifierGroup) => ({ ...g, options: (g.options || []).map(o => ({ ...o })) }))
      : []);
    setIsCreateModalOpen(true);
  };

  const handleDelete = (item: any) => {
    setConfirmationModal({
      isOpen: true,
      title: 'Delete Item',
      message: `Are you sure you want to delete "${item.name}"? This action cannot be undone.`,
      onConfirm: () => {
        deleteMutation.mutate(item.id);
        setConfirmationModal(prev => ({ ...prev, isOpen: false }));
      },
      type: 'danger',
      confirmText: 'Delete'
    });
  };

  if (isLoading) {
    return <PageSkeleton type="grid" />;
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
        <div className="flex items-center gap-3.5 mb-4 sm:mb-0">
          <div className="grid place-items-center h-12 w-12 rounded-2xl text-white shrink-0"
               style={{ background: `linear-gradient(135deg, ${currentTheme.colors.primary[500]}, ${currentTheme.colors.primary[700]})`, boxShadow: `0 14px 32px -12px ${currentTheme.colors.primary[500]}` }}>
            <UtensilsCrossed className="h-6 w-6" />
          </div>
          <div>
            <h1 className="text-2xl font-extrabold tracking-tight" style={{ color: currentTheme.colors.text.primary }}>Menu Items</h1>
            <p className="text-sm mt-0.5" style={{ color: currentTheme.colors.text.secondary }}>Manage your restaurant menu items and pricing</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setIsCategoryModalOpen(true)}
            className="inline-flex items-center px-4 py-2 border rounded-lg transition-colors"
            style={{
              borderColor: currentTheme.colors.border.primary,
              backgroundColor: currentTheme.colors.background.secondary,
              color: currentTheme.colors.text.primary
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = currentTheme.colors.background.card}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = currentTheme.colors.background.secondary}
          >
            <Settings className="h-5 w-5 mr-2" />
            Manage Categories
          </button>
          <button
            onClick={() => {
              setEditingItem(null);
              reset({
                name: '',
                price: 0,
                kitchenId: '',
                categoryId: '',
                vegType: '' as VegType,
                available: true,
                gstPercent: 0
              });
              setIsCreateModalOpen(true);
            }}
            className="inline-flex items-center gap-2 px-5 py-2.5 font-semibold text-white rounded-xl transition-transform hover:scale-[1.03] active:scale-[0.98]"
            style={{ background: `linear-gradient(135deg, ${currentTheme.colors.primary[500]}, ${currentTheme.colors.primary[700]})`, boxShadow: `0 12px 26px -12px ${currentTheme.colors.primary[500]}` }}
          >
            <Plus className="h-5 w-5" />
            Add Item
          </button>
        </div>
      </div>

      {/* Warning: no restaurant selected */}
      {!activeRestaurantId && (
        <div className="mb-4 flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          <span className="mt-0.5 text-amber-500">⚠️</span>
          <span>
            No restaurant is selected. Items created now will not be tagged to a specific restaurant and may not appear on QR menus. Select a restaurant from your profile first.
          </span>
        </div>
      )}

      {/* Filters */}
      <div
        className="rounded-lg border p-4 mb-6"
        style={{ 
          backgroundColor: currentTheme.colors.background.card,
          borderColor: currentTheme.colors.border.primary 
        }}
      >
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5" style={{ color: currentTheme.colors.text.secondary }} />
            <input
              type="text"
              placeholder="Search items..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2"
              style={{ 
                borderColor: currentTheme.colors.border.primary,
                backgroundColor: currentTheme.colors.background.secondary,
                color: currentTheme.colors.text.primary,
                outline: 'none'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = currentTheme.colors.primary[500];
                e.target.style.boxShadow = `0 0 0 2px ${currentTheme.colors.primary[100]}`;
              }}
              onBlur={(e) => {
                e.target.style.borderColor = currentTheme.colors.border.primary;
                e.target.style.boxShadow = 'none';
              }}
            />
          </div>
          <UiSelect
            value={selectedKitchen}
            onChange={setSelectedKitchen}
            options={[{ value: '', label: 'All Kitchens' }, ...(kitchens || []).map(k => ({ value: k.id, label: k.name }))]}
            className="w-full sm:w-48 shrink-0"
          />
          <UiSelect
            value={selectedVegType}
            onChange={setSelectedVegType}
            options={[{ value: '', label: 'All Veg Types' }, ...vegTypes.map(t => ({ value: t, label: t.replace('_', ' ') }))]}
            className="w-full sm:w-44 shrink-0"
          />
          <UiSelect
            value={selectedCategory}
            onChange={setSelectedCategory}
            options={[{ value: '', label: 'All Item Categories' }, ...(categories || []).filter(c => c.active).map(c => ({ value: c.id, label: c.name }))]}
            className="w-full sm:w-52 shrink-0"
          />
        </div>
      </div>

      {/* Hidden file input for image upload */}
      <input
        ref={imageInputRef}
        type="file"
        accept=".jpg,.jpeg,.png"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file && uploadingImageItemId) {
            handleImageUpload(uploadingImageItemId, file);
          }
          e.target.value = '';
        }}
      />

      {/* Items Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredItems.map((item) => (
          <div
            key={item.id}
            className="rounded-2xl border transition-all duration-200 hover:-translate-y-1 bg-white overflow-hidden shadow-[0_1px_2px_rgba(16,24,40,0.04),0_12px_30px_-16px_rgba(16,24,40,0.20)]"
            style={{ borderColor: currentTheme.colors.border.primary }}
          >
            {/* Images row */}
            {(item.images && item.images.length > 0) ? (
              <div className="flex gap-1 p-2 pb-0">
                {item.images.map((imgUrl, idx) => (
                  <div key={idx} className="relative group w-16 h-16 rounded-lg overflow-hidden flex-shrink-0">
                    <img src={imgUrl} alt={`${item.name} ${idx + 1}`} className="w-full h-full object-cover" />
                    <button
                      onClick={() => deleteImageMutation.mutate({ id: item.id, url: imgUrl })}
                      className="absolute inset-0 bg-black bg-opacity-50 hidden group-hover:flex items-center justify-center rounded-lg"
                      title="Remove image"
                    >
                      <X className="h-4 w-4 text-white" />
                    </button>
                  </div>
                ))}
                {item.images.length < 4 && (
                  <button
                    onClick={() => { setUploadingImageItemId(item.id); imageInputRef.current?.click(); }}
                    disabled={uploadingImageItemId === item.id && uploadImageMutation.isLoading}
                    className="w-16 h-16 rounded-lg border-2 border-dashed flex items-center justify-center flex-shrink-0 transition-colors"
                    style={{ borderColor: currentTheme.colors.border.primary, color: currentTheme.colors.text.secondary }}
                    title="Add image"
                  >
                    {uploadingImageItemId === item.id && uploadImageMutation.isLoading
                      ? <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                      : <ImagePlus className="h-5 w-5" />}
                  </button>
                )}
              </div>
            ) : (
              <div className="px-2 pt-2">
                <button
                  onClick={() => { setUploadingImageItemId(item.id); imageInputRef.current?.click(); }}
                  disabled={uploadingImageItemId === item.id && uploadImageMutation.isLoading}
                  className="w-full h-20 rounded-lg border-2 border-dashed flex items-center justify-center gap-2 text-sm transition-colors"
                  style={{ borderColor: currentTheme.colors.border.primary, color: currentTheme.colors.text.secondary }}
                >
                  {uploadingImageItemId === item.id && uploadImageMutation.isLoading ? (
                    <><div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" /> Uploading...</>
                  ) : (
                    <><ImagePlus className="h-4 w-4" /> Add Photo</>
                  )}
                </button>
              </div>
            )}

            {/* Header with Item Name and Availability */}
            <div className="flex items-center justify-between p-6 pb-4">
              <div className="flex items-center">
                <div className="p-2 rounded-lg mr-3" style={{ backgroundColor: currentTheme.colors.primary[100] }}>
                  <UtensilsCrossed className="h-5 w-5" style={{ color: currentTheme.colors.primary[600] }} />
                </div>
                <div>
                  <h3 className="text-lg font-semibold" style={{ color: currentTheme.colors.text.primary }}>
                    {item.name}
                  </h3>
                  <p className="text-sm" style={{ color: currentTheme.colors.text.secondary }}>
                    {item.kitchenName} • ₹{item.price} {item.gstPercent ? `• GST ${item.gstPercent}%` : ''}
                  </p>
                </div>
              </div>
              <span className={clsx(
                'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium',
                item.available ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
              )}>
                {item.available ? 'Available' : 'Out of Stock'}
              </span>
            </div>

            {/* Item Details */}
            <div className="px-6 mb-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium" style={{ color: currentTheme.colors.text.secondary }}>
                  Category:
                </span>
                <div className="flex items-center space-x-2">
                  <div className="flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium" style={{ backgroundColor: currentTheme.colors.background.secondary }}>
                    {item.vegType === 'VEG' && <img src={VegIcon} alt="Veg" className="h-4 w-4" />}
                    {item.vegType === 'NON_VEG' && <img src={NonVegIcon} alt="Non-Veg" className="h-4 w-4" />}
                    {item.vegType === 'VEGAN' && <img src={VegIcon} alt="Vegan" className="h-4 w-4" />}
                    <span style={{ color: currentTheme.colors.text.primary }}>{item.vegType.replace('_', '-')}</span>
                  </div>
                  {item.categoryId && (
                    <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
                      {categories?.find(c => c.id === item.categoryId)?.name || 'Unknown'}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center justify-between px-6 pb-6 pt-4 border-t" style={{ borderColor: currentTheme.colors.border.primary }}>
              <button
                onClick={() => handleToggleAvailability(item)}
                className="px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                style={{
                  backgroundColor: item.available ? '#fef2f2' : '#f0fdf4',
                  color: item.available ? '#dc2626' : '#16a34a',
                  border: `1px solid ${item.available ? '#fecaca' : '#bbf7d0'}`
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = item.available ? '#fee2e2' : '#dcfce7';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = item.available ? '#fef2f2' : '#f0fdf4';
                }}
                title={item.available ? 'Click to make item unavailable' : 'Click to make item available'}
              >
                {item.available ? 'Make Unavailable' : 'Make Available'}
              </button>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handleEdit(item)}
                  className="p-2 rounded-lg transition-colors"
                  style={{
                    color: currentTheme.colors.primary[600],
                    backgroundColor: currentTheme.colors.primary[50]
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = currentTheme.colors.primary[100]}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = currentTheme.colors.primary[50]}
                  title="Edit Item"
                >
                  <Edit className="h-4 w-4" />
                </button>
                <button
                  onClick={() => handleDelete(item)}
                  className="p-2 rounded-lg transition-colors bg-red-50"
                  style={{ color: '#dc2626' }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#fef2f2'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#fef2f2'}
                  title="Delete Item"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredItems.length === 0 && (
        <EmptyState
          icon={<UtensilsCrossed className="h-16 w-16" style={{ color: currentTheme.colors.text.secondary }} />}
          title="No Items Found"
          description={searchTerm ? 'Try adjusting your search or filters.' : 'Get started by adding your first menu item.'}
          action={
            !searchTerm ? (
              <button
                onClick={() => {
                  setEditingItem(null);
                  reset({
                    name: '',
                    price: 0,
                    kitchenId: '',
                    categoryId: '',
                    vegType: '' as VegType,
                    available: true,
                    gstPercent: 0
                  });
                  setIsCreateModalOpen(true);
                }}
                className="inline-flex items-center px-4 py-2 text-white rounded-lg btn-primary transition-colors"
                style={{ backgroundColor: currentTheme.colors.primary[600] }}
              >
                <Plus className="h-5 w-5 mr-2" />
                Add Item
              </button>
            ) : undefined
          }
        />
      )}

      {/* Create Item Modal */}
      {isCreateModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900">
                {editingItem ? 'Edit Menu Item' : 'Add New Menu Item'}
              </h2>
              <button
                onClick={() => {
                  setIsCreateModalOpen(false);
                  setEditingItem(null);
                  reset();
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              {/* Item Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Item Name</label>
                <input
                  {...register('name', { required: 'Item name is required' })}
                  type="text"
                  className="ui-input"
                  placeholder="e.g., Margherita Pizza"
                />
                {errors.name && (
                  <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
                )}
              </div>

              {/* Price and Kitchen */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Price (₹)</label>
                  <input
                    {...register('price', { 
                      required: 'Price is required',
                      min: { value: 1, message: 'Price must be at least ₹1' },
                      valueAsNumber: true
                    })}
                    type="number"
                    step="0.01"
                    className="ui-input"
                    placeholder="0.00"
                  />
                  {errors.price && (
                    <p className="mt-1 text-sm text-red-600">{errors.price.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Kitchen</label>
                  <Controller
                    name="kitchenId"
                    control={control}
                    rules={{ required: 'Kitchen is required' }}
                    render={({ field }) => (
                      <UiSelect
                        value={field.value || ''}
                        onChange={field.onChange}
                        placeholder="Select Kitchen"
                        options={[{ value: '', label: 'Select Kitchen' }, ...(kitchens || []).map(k => ({ value: k.id, label: k.name }))]}
                      />
                    )}
                  />
                  {errors.kitchenId && (
                    <p className="mt-1 text-sm text-red-600">{errors.kitchenId.message}</p>
                  )}
                </div>
              </div>

              {/* Category and Veg Type */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                  <Controller
                    name="categoryId"
                    control={control}
                    rules={{ required: 'Category is required' }}
                    render={({ field }) => (
                      <UiSelect
                        value={field.value || ''}
                        onChange={field.onChange}
                        placeholder="Select Category"
                        options={[{ value: '', label: 'Select Category' }, ...(categories || []).filter(c => c.active).map(c => ({ value: c.id, label: c.name }))]}
                      />
                    )}
                  />
                  {errors.categoryId && (
                    <p className="mt-1 text-sm text-red-600">{errors.categoryId.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Veg Type</label>
                  <Controller
                    name="vegType"
                    control={control}
                    rules={{ required: 'Veg type is required' }}
                    render={({ field }) => (
                      <UiSelect
                        value={field.value || ''}
                        onChange={field.onChange}
                        placeholder="Select Veg Type"
                        options={[{ value: '', label: 'Select Veg Type' }, ...vegTypes.map(t => ({ value: t, label: t.replace('_', ' ') }))]}
                      />
                    )}
                  />
                  {errors.vegType && (
                    <p className="mt-1 text-sm text-red-600">{errors.vegType.message}</p>
                  )}
                </div>
              </div>

              {/* GST % and HSN Code */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">GST %</label>
                  <input
                    {...register('gstPercent', {
                      min: { value: 0, message: 'GST must be 0 or greater' },
                      max: { value: 100, message: 'GST cannot exceed 100%' },
                      valueAsNumber: true
                    })}
                    type="number"
                    step="0.01"
                    className="ui-input"
                    placeholder="e.g., 5, 12, 18"
                  />
                  {errors.gstPercent && (
                    <p className="mt-1 text-sm text-red-600">{errors.gstPercent.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">HSN Code (Optional)</label>
                  <input
                    {...register('hsnCode')}
                    type="text"
                    className="ui-input"
                    placeholder="e.g., 99604010"
                  />
                </div>
              </div>

              {/* Available */}
              <div className="flex items-center">
                <input
                  {...register('available')}
                  type="checkbox"
                  defaultChecked={true}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <label className="ml-2 block text-sm text-gray-900">
                  Item is available for ordering
                </label>
              </div>

              {/* Variants */}
              <div className="border-t pt-4" style={{ borderColor: currentTheme.colors.border.primary }}>
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <label className="block text-sm font-semibold text-gray-800">Variants (sizes / portions)</label>
                    <p className="text-xs text-gray-500">Each variant has its own price. If none, the base price is used.</p>
                  </div>
                  <button type="button" onClick={() => setVariants(v => [...v, { name: '', price: 0 }])}
                    className="text-sm font-medium flex items-center gap-1" style={{ color: currentTheme.colors.primary[600] }}>
                    <Plus className="h-4 w-4" /> Add variant
                  </button>
                </div>
                <div className="space-y-2">
                  {variants.map((v, i) => (
                    <div key={i} className="flex gap-2 items-center">
                      <input value={v.name} onChange={e => setVariants(list => list.map((x, j) => j === i ? { ...x, name: e.target.value } : x))}
                        placeholder="e.g., Half, Full, Large" className="flex-1 px-3 py-2 border border-gray-300 rounded-lg" />
                      <input type="number" step="any" min="0" value={v.price}
                        onChange={e => setVariants(list => list.map((x, j) => j === i ? { ...x, price: Number(e.target.value) } : x))}
                        placeholder="Price ₹" className="w-32 px-3 py-2 border border-gray-300 rounded-lg" />
                      <button type="button" onClick={() => setVariants(list => list.filter((_, j) => j !== i))}
                        className="p-2 rounded-lg text-red-500 hover:bg-red-50"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Modifier groups */}
              <div className="border-t pt-4" style={{ borderColor: currentTheme.colors.border.primary }}>
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <label className="block text-sm font-semibold text-gray-800">Add-on Groups (modifiers)</label>
                    <p className="text-xs text-gray-500">e.g., "Extra toppings". Each option adds its price to the item.</p>
                  </div>
                  <button type="button" onClick={() => setModifierGroups(g => [...g, { name: '', required: false, minSelect: 0, maxSelect: 0, options: [{ name: '', price: 0 }] }])}
                    className="text-sm font-medium flex items-center gap-1" style={{ color: currentTheme.colors.primary[600] }}>
                    <Plus className="h-4 w-4" /> Add group
                  </button>
                </div>
                <div className="space-y-3">
                  {modifierGroups.map((g, gi) => (
                    <div key={gi} className="rounded-lg border p-3" style={{ borderColor: currentTheme.colors.border.primary }}>
                      <div className="flex gap-2 items-center mb-2">
                        <input value={g.name} onChange={e => setModifierGroups(list => list.map((x, j) => j === gi ? { ...x, name: e.target.value } : x))}
                          placeholder="Group name (e.g., Toppings)" className="flex-1 px-3 py-2 border border-gray-300 rounded-lg" />
                        <label className="flex items-center gap-1 text-xs text-gray-600 whitespace-nowrap">
                          <input type="checkbox" checked={g.required} onChange={e => setModifierGroups(list => list.map((x, j) => j === gi ? { ...x, required: e.target.checked } : x))} />
                          Required
                        </label>
                        <button type="button" onClick={() => setModifierGroups(list => list.filter((_, j) => j !== gi))}
                          className="p-2 rounded-lg text-red-500 hover:bg-red-50"><Trash2 className="h-4 w-4" /></button>
                      </div>
                      <div className="flex gap-2 items-center mb-2 text-xs text-gray-600">
                        <span>Min</span>
                        <input type="number" min="0" value={g.minSelect ?? 0} onChange={e => setModifierGroups(list => list.map((x, j) => j === gi ? { ...x, minSelect: Number(e.target.value) } : x))} className="w-16 px-2 py-1 border border-gray-300 rounded" />
                        <span>Max (0 = unlimited)</span>
                        <input type="number" min="0" value={g.maxSelect ?? 0} onChange={e => setModifierGroups(list => list.map((x, j) => j === gi ? { ...x, maxSelect: Number(e.target.value) } : x))} className="w-16 px-2 py-1 border border-gray-300 rounded" />
                      </div>
                      <div className="space-y-1.5 pl-2">
                        {g.options.map((o, oi) => (
                          <div key={oi} className="flex gap-2 items-center">
                            <input value={o.name} onChange={e => setModifierGroups(list => list.map((x, j) => j === gi ? { ...x, options: x.options.map((y, k) => k === oi ? { ...y, name: e.target.value } : y) } : x))}
                              placeholder="Option (e.g., Cheese)" className="flex-1 px-3 py-1.5 border border-gray-300 rounded-lg text-sm" />
                            <input type="number" step="any" min="0" value={o.price} onChange={e => setModifierGroups(list => list.map((x, j) => j === gi ? { ...x, options: x.options.map((y, k) => k === oi ? { ...y, price: Number(e.target.value) } : y) } : x))}
                              placeholder="+₹" className="w-24 px-3 py-1.5 border border-gray-300 rounded-lg text-sm" />
                            <button type="button" onClick={() => setModifierGroups(list => list.map((x, j) => j === gi ? { ...x, options: x.options.filter((_, k) => k !== oi) } : x))}
                              className="p-1.5 rounded-lg text-red-400 hover:bg-red-50"><X className="h-4 w-4" /></button>
                          </div>
                        ))}
                        <button type="button" onClick={() => setModifierGroups(list => list.map((x, j) => j === gi ? { ...x, options: [...x.options, { name: '', price: 0 }] } : x))}
                          className="text-xs font-medium flex items-center gap-1 mt-1" style={{ color: currentTheme.colors.primary[600] }}>
                          <Plus className="h-3.5 w-3.5" /> Add option
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setIsCreateModalOpen(false);
                    setEditingItem(null);
                    reset();
                  }}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isLoading || updateMutation.isLoading}
                  className="px-5 py-2.5 text-white font-bold rounded-xl transition-transform hover:scale-[1.03] active:scale-[0.98] disabled:opacity-50 disabled:hover:scale-100 flex items-center"
                  style={{ background: `linear-gradient(135deg, ${currentTheme.colors.primary[500]}, ${currentTheme.colors.primary[700]})`, boxShadow: `0 12px 26px -12px ${currentTheme.colors.primary[500]}` }}
                >
                  {(createMutation.isLoading || updateMutation.isLoading) ? (
                    <ButtonLoader text={editingItem ? 'Updating...' : 'Creating...'} />
                  ) : (
                    editingItem ? 'Update Item' : 'Create Item'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Category Management Modal */}
      {isCategoryModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div
            className="rounded-xl max-w-lg w-full max-h-[80vh] overflow-hidden flex flex-col"
            style={{ backgroundColor: currentTheme.colors.background.card }}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b" style={{ borderColor: currentTheme.colors.border.primary }}>
              <h2 className="text-lg font-semibold" style={{ color: currentTheme.colors.text.primary }}>
                Manage Item Categories
              </h2>
              <button
                onClick={() => {
                  setIsCategoryModalOpen(false);
                  setEditingCategory(null);
                  setCategoryName('');
                }}
                className="p-1 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <X className="h-5 w-5" style={{ color: currentTheme.colors.text.secondary }} />
              </button>
            </div>

            {/* Add/Edit Category Form */}
            <div className="p-4 border-b" style={{ borderColor: currentTheme.colors.border.primary }}>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={categoryName}
                  onChange={(e) => setCategoryName(e.target.value)}
                  placeholder={editingCategory ? "Edit category name" : "New category name"}
                  className="flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:border-transparent"
                  style={{
                    borderColor: currentTheme.colors.border.primary,
                    backgroundColor: currentTheme.colors.background.secondary,
                    color: currentTheme.colors.text.primary
                  }}
                />
                {editingCategory ? (
                  <>
                    <button
                      onClick={() => {
                        if (categoryName.trim()) {
                          updateCategoryMutation.mutate({ id: editingCategory.id, data: { name: categoryName.trim() } });
                        }
                      }}
                      disabled={!categoryName.trim() || updateCategoryMutation.isLoading}
                      className="px-4 py-2 text-white rounded-lg btn-primary transition-colors disabled:opacity-50"
                      style={{ backgroundColor: currentTheme.colors.primary[600] }}
                    >
                      {updateCategoryMutation.isLoading ? <ButtonLoader text="Saving..." /> : 'Update'}
                    </button>
                    <button
                      onClick={() => {
                        setEditingCategory(null);
                        setCategoryName('');
                      }}
                      className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                    >
                      Cancel
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => {
                      if (categoryName.trim()) {
                        createCategoryMutation.mutate({
                          name: categoryName.trim(),
                          organizationCode: user?.organizationCode!,
                          ...(activeRestaurantId && { restaurantId: activeRestaurantId }),
                        });
                      }
                    }}
                    disabled={!categoryName.trim() || createCategoryMutation.isLoading}
                    className="px-4 py-2 text-white rounded-lg btn-primary transition-colors disabled:opacity-50 flex items-center gap-2"
                    style={{ backgroundColor: currentTheme.colors.primary[600] }}
                  >
                    {createCategoryMutation.isLoading ? <ButtonLoader text="Adding..." /> : (
                      <>
                        <Plus className="h-4 w-4" />
                        Add
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>

            {/* Categories List */}
            <div className="flex-1 overflow-y-auto p-4">
              {categories && categories.length > 0 ? (
                <div className="space-y-2">
                  {categories.map((category) => (
                    <div
                      key={category.id}
                      className="flex items-center justify-between p-3 rounded-lg border"
                      style={{
                        borderColor: currentTheme.colors.border.primary,
                        backgroundColor: category.active ? currentTheme.colors.background.secondary : '#f3f4f6',
                        opacity: category.active ? 1 : 0.7
                      }}
                    >
                      <div className="flex items-center gap-3">
                        <span
                          className="font-medium"
                          style={{ color: currentTheme.colors.text.primary }}
                        >
                          {category.name}
                        </span>
                        {!category.active && (
                          <span className="text-xs px-2 py-0.5 bg-red-100 text-red-600 rounded-full">
                            Inactive
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => {
                            setEditingCategory(category);
                            setCategoryName(category.name);
                          }}
                          className="p-1.5 rounded-lg hover:bg-gray-200 transition-colors"
                          title="Edit"
                        >
                          <Edit className="h-4 w-4" style={{ color: currentTheme.colors.primary[600] }} />
                        </button>
                        <button
                          onClick={() => toggleCategoryMutation.mutate(category.id)}
                          disabled={toggleCategoryMutation.isLoading}
                          className={clsx(
                            "p-1.5 rounded-lg transition-colors",
                            category.active ? "hover:bg-red-100" : "hover:bg-green-100"
                          )}
                          title={category.active ? "Deactivate" : "Activate"}
                        >
                          {category.active ? (
                            <X className="h-4 w-4 text-red-500" />
                          ) : (
                            <Check className="h-4 w-4 text-green-500" />
                          )}
                        </button>
                        <button
                          onClick={() => {
                            if (window.confirm(`Are you sure you want to delete "${category.name}"?`)) {
                              deleteCategoryMutation.mutate(category.id);
                            }
                          }}
                          disabled={deleteCategoryMutation.isLoading}
                          className="p-1.5 rounded-lg hover:bg-red-100 transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <p style={{ color: currentTheme.colors.text.secondary }}>
                    No categories yet. Add your first category above.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={confirmationModal.isOpen}
        onClose={() => setConfirmationModal(prev => ({ ...prev, isOpen: false }))}
        onConfirm={confirmationModal.onConfirm}
        title={confirmationModal.title}
        message={confirmationModal.message}
        confirmText={confirmationModal.confirmText}
        type={confirmationModal.type}
        isLoading={toggleAvailabilityMutation.isLoading || deleteMutation.isLoading}
      />
    </div>
  );
};